import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import Ripple from '@/components/Ripple'
import RefreshList from '@/components/RefreshList'
import { Toast, Checkbox } from 'react-vant'
import { APIWeb } from '@/api'

export default React.memo(function PayeeList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  // 选中的收款人
  const [selectedPayee, setSelectedPayee] = useState<ModelsPaymentAccount | null>(null)

  // 判断是否从汇款页面进入（用于决定点击确定后跳转到哪里）
  const fromRemittance = useMemo(() => {
    return location.state?.selectMode === true
  }, [location.state])

  // 列表数据
  const [payeeList, setPayeeList] = useState<ModelsPaymentAccount[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // 获取币种图标
  const getCurrencyIcon = useCallback((currency: string) => {
    const currencyMap: Record<string, string> = {
      'USDT': '/images/home/usdt.png',
      'USDC': '/images/home/usdc.png',
      'USD1': '/images/home/usd1.png',
      'USD': '/images/home/usd.png',
    }
    return currencyMap[currency.toUpperCase()] || '/images/home/usdt.png'
  }, [])

  // 获取收款账户列表
  const fetchPayeeList = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading) return

    try {
      setLoading(true)
      const data = await APIWeb.getReceivingAccountList({
        page,
        page_size: pageSize
      })

      const newList = data?.list || []

      if (isAppend) {
        setPayeeList(prev => [...prev, ...newList])
      } else {
        setPayeeList(newList)
      }

      // 判断是否还有更多数据
      setHasMore(newList.length >= pageSize)
      setCurrentPage(page)
    } catch (error) {
      console.error('获取收款账户列表失败:', error)
    } finally {
      setLoading(false)
    }
  }, [loading, pageSize])

  // 下拉刷新
  const handleRefresh = useCallback(async () => {
    await fetchPayeeList(1, false)
  }, [fetchPayeeList])

  // 加载更多
  const handleLoadMore = useCallback(async () => {
    if (hasMore && !loading) {
      await fetchPayeeList(currentPage + 1, true)
    }
  }, [hasMore, loading, currentPage, fetchPayeeList])

  // 添加收款人
  const handleAddPayee = useCallback(() => {
    navigate('/remittance/add-payee')
  }, [navigate])

  // 选择/取消选择收款人（点击同一项时取消选择）
  const handleSelectPayee = useCallback((payee: ModelsPaymentAccount) => {
    setSelectedPayee(prev => {
      // 如果点击的是已选中的项，则取消选择
      if (prev?.id === payee.id) {
        return null
      }
      // 否则选中该项
      return payee
    })
  }, [])

  // 确认选择（只有从汇款页面进入时才跳转回去）
  const handleConfirmSelect = useCallback(() => {
    if (selectedPayee) {
      if (fromRemittance) {
        // 从汇款页面进入，返回并传递数据
        navigate('/remittance/transfer', { 
          state: { 
            selectedPayee 
          },
          replace: true
        })
      } else {
        // 直接进入收款人列表，可以跳转到汇款页面
        navigate('/remittance/transfer', {
          state: {
            selectedPayee
          }
        })
      }
    } else {
      Toast.info(t('请选择收款人'))
    }
  }, [selectedPayee, fromRemittance, navigate, t])

  // 点击收款人项（点击 checkbox 或整行）
  const handleItemClick = useCallback((payee: ModelsPaymentAccount) => {
    handleSelectPayee(payee)
  }, [handleSelectPayee])

  // 空状态
  const EmptyState = useMemo(() => (
    <div className="flex flex-col items-center justify-center pt-40">
      {/* 空状态图标 */}
      <div className="w-24 h-24 mb-6 flex items-center justify-center">
        <img src="/icon/no-address-book.svg" alt="" />
      </div>

      {/* 空状态文字 */}
      <div className="text-base text-gray-400 mb-8">
        {t('暂无收款人')}
      </div>

      {/* 添加按钮 */}
      <Ripple className="rounded-full">
        <button
          onClick={handleAddPayee}
          className="px-12 py-3 border-2 border-[var(--primary-color)] border-solid border text-[var(--primary-color)] text-base font-medium rounded-full"
        >
          +{t('添加收款人')}
        </button>
      </Ripple>
    </div>
  ), [t, handleAddPayee])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={
          <span className="text-3.75 font-medium text-black">{t('收款人')}</span>
        }
        rightChildren={
          selectedPayee ? (
            // 已选择收款人，显示“确定”按钮
            <button
              onClick={handleConfirmSelect}
              className="text-[var(--primary-color)] text-sm font-medium"
            >
              {t('确定')}
            </button>
          ) : (
            // 未选择，显示“添加”按钮
            <Ripple className="rounded-full">
              <button
                onClick={handleAddPayee}
                className="w-10 h-10 flex items-center justify-center bg-[var(--primary-color)] rounded-full"
              >
                <img src="/icon/white-add.svg" alt="" />
              </button>
            </Ripple>
          )
        }
      />

      <div className="bg-white flex-1 overflow-hidden">
        <RefreshList
          data={payeeList}
          loading={loading}
          hasMore={hasMore}
          onRefresh={handleRefresh}
          onLoadMore={handleLoadMore}
          emptyText={t('暂无收款人')}
          finishedText={t('没有更多数据了')}
          emptyComponent={EmptyState}
          className="px-6 py-6"
          itemClassName="mb-4"
          renderItem={(payee) => (
            <div
              onClick={() => handleItemClick(payee)}
              className={`bg-white border-0.5 border-solid mb-4 rounded-2xl p-4 relative cursor-pointer transition-colors ${
                selectedPayee?.id === payee.id 
                  ? 'border-[var(--primary-color)]' 
                  : 'border-[#E5E5E5]'
              }`}
            >
              {/* Checkbox（始终显示） */}
              <div className="absolute bottom-4 right-4">
                <Checkbox
                  checked={selectedPayee?.id === payee.id}
                  iconSize="1.5rem"
                />
              </div>

              {/* 银行名称和账户币种 */}
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-black">
                  {payee.bank_name}
                </div>
                <div className="flex items-center gap-2 text-4 font-medium px-2 py-1 rounded">
                  <img 
                    src={getCurrencyIcon(payee.account_ccy || 'USD')} 
                    alt={payee.account_ccy} 
                    className="w-6 h-6"
                  />
                  <span>{payee.account_ccy}</span>
                </div>
              </div>

              {/* 账户名 */}
              <div className="text-xs text-[#0F0F0F]/60 mb-2">
                {t('账户名')}: {payee.account_name}
              </div>

              {/* 银行账户 */}
              <div className="text-xs text-[#0F0F0F]/60 mb-2">
                {t('银行账户')}: {payee.bank_account}
              </div>

              {/* 银行所在国家 */}
              <div className="text-xs text-[#0F0F0F]/60 mb-2">
                {t('银行所在国家或地区')}: {payee.bank_country}
              </div>

              {/* SWIFT Code */}
              <div className="text-xs text-[#0F0F0F]/60">
                SWIFT Code: {payee.swift_code}
              </div>
            </div>
          )}
          keyExtractor={(payee) => String(payee.id)}
        />
      </div>
    </PageLayout>
  )
})
